/** PNBIL Part A screening (A3 Q1–17) → A4 outcome rules and Part B gating. */

import { DPIA_PART_B_CODES } from '../pages/vms/dpiaPartModel';

export const DPIA_A3_CODE = 'PNBIL-D03';
export const DPIA_A4_CODE = 'PNBIL-D04';

const A3_QUESTION_COUNT = 17;
const A4_AUTO_OUTCOME_ROWS = 5;

export type YesNoAnswer = 'Yes' | 'No' | '';

export type DpiaScreeningOutcomeLevel = 'required' | 'recommended' | 'not_required' | 'incomplete';

export type DpiaScreeningOutcome = {
  level: DpiaScreeningOutcomeLevel;
  rule: 1 | 2 | 3 | 4 | null;
  anyQ1to7Yes: boolean;
  yesCountQ8to17: number;
  dpiaRequired: boolean;
  dpiaRecommended: boolean;
  partBRequired: boolean;
  partBMandatory: boolean;
  partBVisible: boolean;
  badgeLabel: string;
  mandatoryWarning: string | null;
  recommendationMessage: string | null;
};

export const DPIA_SCREENING_MANDATORY_WARNING =
  'DPIA is mandatory and Part B must be completed based on the screening assessment outcome.';

export const DPIA_SCREENING_RECOMMENDED_MESSAGE =
  'A DPIA is recommended but not mandatory. You may proceed without completing Part B unless the DPO or business decision requires it.';

export function a3YesNoCellKey(questionNumber: number): string {
  return `a3_r${String(questionNumber).padStart(2, '0')}_a3_yn`;
}

export function a4YesNoCellKey(outcomeRow: number): string {
  return `a4_r${outcomeRow}_a4_yn`;
}

export const A4_AUTO_YESNO_CELL_KEYS: readonly string[] = Array.from(
  { length: A4_AUTO_OUTCOME_ROWS },
  (_, i) => a4YesNoCellKey(i + 1)
);

export const A4_AUTO_YESNO_CELL_KEY_SET = new Set(A4_AUTO_YESNO_CELL_KEYS);

export function parseYesNoAnswer(value: unknown): YesNoAnswer {
  if (value == null) return '';
  const s = String(value).trim();
  if (s === 'Yes' || s.toLowerCase() === 'yes') return 'Yes';
  if (s === 'No' || s.toLowerCase() === 'no') return 'No';
  return '';
}

export function readA3Answers(response: Record<string, unknown> | null | undefined): YesNoAnswer[] {
  const answers: YesNoAnswer[] = [];
  for (let i = 1; i <= A3_QUESTION_COUNT; i += 1) {
    answers.push(parseYesNoAnswer(response?.[a3YesNoCellKey(i)]));
  }
  return answers;
}

export function isA3ScreeningComplete(answers: YesNoAnswer[]): boolean {
  return answers.every((a) => a === 'Yes' || a === 'No');
}

function recommendedOutcome(yesCountQ8to17: number): DpiaScreeningOutcome {
  return {
    level: 'recommended',
    rule: 3,
    anyQ1to7Yes: false,
    yesCountQ8to17,
    dpiaRequired: false,
    dpiaRecommended: true,
    partBRequired: false,
    partBMandatory: false,
    partBVisible: true,
    badgeLabel: 'DPIA Recommended',
    mandatoryWarning: null,
    recommendationMessage: DPIA_SCREENING_RECOMMENDED_MESSAGE,
  };
}

function notRequiredOutcome(): DpiaScreeningOutcome {
  return {
    level: 'not_required',
    rule: 4,
    anyQ1to7Yes: false,
    yesCountQ8to17: 0,
    dpiaRequired: false,
    dpiaRecommended: false,
    partBRequired: false,
    partBMandatory: false,
    partBVisible: false,
    badgeLabel: 'DPIA Not Required',
    mandatoryWarning: null,
    recommendationMessage: null,
  };
}

function incompleteOutcome(): DpiaScreeningOutcome {
  return {
    level: 'incomplete',
    rule: null,
    anyQ1to7Yes: false,
    yesCountQ8to17: 0,
    dpiaRequired: false,
    dpiaRecommended: false,
    partBRequired: false,
    partBMandatory: false,
    partBVisible: false,
    badgeLabel: 'Screening incomplete',
    mandatoryWarning: null,
    recommendationMessage: null,
  };
}

export function computeDpiaScreeningOutcome(
  a3Response: Record<string, unknown> | null | undefined
): DpiaScreeningOutcome {
  const answers = readA3Answers(a3Response);
  const q1to7 = answers.slice(0, 7);
  const q8to17 = answers.slice(7, 17);
  const anyQ1to7Yes = q1to7.some((a) => a === 'Yes');
  const yesCountQ8to17 = q8to17.filter((a) => a === 'Yes').length;
  const allComplete = isA3ScreeningComplete(answers);

  if (anyQ1to7Yes) {
    return requiredOutcome(1, anyQ1to7Yes, yesCountQ8to17);
  }
  if (yesCountQ8to17 >= 2) {
    return requiredOutcome(2, anyQ1to7Yes, yesCountQ8to17);
  }
  // Rule 3: mirror rule 2 — one Yes in Q8–17 triggers recommended (Q8–17 need not all be answered).
  if (!anyQ1to7Yes && yesCountQ8to17 === 1) {
    return recommendedOutcome(1);
  }
  if (allComplete) {
    return notRequiredOutcome();
  }
  return incompleteOutcome();
}

function requiredOutcome(
  rule: 1 | 2,
  anyQ1to7Yes: boolean,
  yesCountQ8to17: number
): DpiaScreeningOutcome {
  return {
    level: 'required',
    rule,
    anyQ1to7Yes,
    yesCountQ8to17,
    dpiaRequired: true,
    dpiaRecommended: false,
    partBRequired: true,
    partBMandatory: true,
    partBVisible: true,
    badgeLabel: 'DPIA Required',
    mandatoryWarning: DPIA_SCREENING_MANDATORY_WARNING,
    recommendationMessage: null,
  };
}

export function buildA4AutoResponsePatch(
  a3Response: Record<string, unknown> | null | undefined
): Record<string, string> {
  const answers = readA3Answers(a3Response);
  const q1to7 = answers.slice(0, 7);
  const q8to17 = answers.slice(7, 17);
  const anyQ1to7Yes = q1to7.some((a) => a === 'Yes');
  const yesCountQ8to17 = q8to17.filter((a) => a === 'Yes').length;
  const allQ1to7Answered = q1to7.every((a) => a === 'Yes' || a === 'No');
  const allQ8to17Answered = q8to17.every((a) => a === 'Yes' || a === 'No');
  const allComplete = isA3ScreeningComplete(answers);
  const allNo = allComplete && answers.every((a) => a === 'No');
  const patch: Record<string, string> = {};

  if (anyQ1to7Yes) {
    patch[a4YesNoCellKey(1)] = 'Yes';
  } else if (allQ1to7Answered) {
    patch[a4YesNoCellKey(1)] = 'No';
  }

  if (anyQ1to7Yes) {
    patch[a4YesNoCellKey(2)] = 'No';
  } else if (yesCountQ8to17 >= 2) {
    patch[a4YesNoCellKey(2)] = 'Yes';
  } else if (allQ8to17Answered) {
    patch[a4YesNoCellKey(2)] = 'No';
  }

  if (anyQ1to7Yes) {
    patch[a4YesNoCellKey(3)] = 'No';
  } else if (yesCountQ8to17 === 1) {
    patch[a4YesNoCellKey(3)] = 'Yes';
  } else if (allQ8to17Answered) {
    patch[a4YesNoCellKey(3)] = 'No';
  }

  if (allNo) {
    patch[a4YesNoCellKey(4)] = 'Yes';
  } else if (allComplete) {
    patch[a4YesNoCellKey(4)] = 'No';
  }

  if (anyQ1to7Yes || yesCountQ8to17 >= 2) {
    patch[a4YesNoCellKey(5)] = 'Yes';
  } else if (
    allComplete ||
    (allQ1to7Answered && allQ8to17Answered) ||
    (!anyQ1to7Yes && yesCountQ8to17 === 1)
  ) {
    patch[a4YesNoCellKey(5)] = 'No';
  }

  return patch;
}

export function resolveA4OutcomeDisplay(
  response: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  const base =
    response != null && typeof response === 'object' && !Array.isArray(response) ? { ...response } : {};
  const patch = buildA4AutoResponsePatch(base);
  for (const key of A4_AUTO_YESNO_CELL_KEYS) {
    if (key in patch) {
      base[key] = patch[key];
    } else {
      delete base[key];
    }
  }
  return base;
}

export function syncA4OutcomeFields(response: Record<string, unknown>): Record<string, unknown> {
  return resolveA4OutcomeDisplay(response);
}

export function mergeA4AutoFields(
  a4Response: Record<string, unknown> | null | undefined,
  a3Response: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  const base =
    a4Response != null && typeof a4Response === 'object' && !Array.isArray(a4Response)
      ? { ...a4Response }
      : {};
  const merged = { ...base, ...a3Response };
  const patch = buildA4AutoResponsePatch(merged);
  for (const key of A4_AUTO_YESNO_CELL_KEYS) {
    if (key in patch) {
      base[key] = patch[key];
    } else {
      delete base[key];
    }
  }
  return base;
}

export function screeningOutcomeFromDpiaRows(
  rows: Array<{ code: string; item: { response?: Record<string, unknown> | null } | null }>
): DpiaScreeningOutcome {
  const a3 = rows.find((r) => r.code === DPIA_A3_CODE)?.item?.response ?? null;
  return computeDpiaScreeningOutcome(a3);
}

export function outcomeBadgeClass(level: DpiaScreeningOutcomeLevel): string {
  switch (level) {
    case 'required':
      return 'bg-red-50 text-red-900 ring-red-200';
    case 'recommended':
      return 'bg-amber-50 text-amber-950 ring-amber-200';
    case 'not_required':
      return 'bg-emerald-50 text-emerald-900 ring-emerald-200';
    default:
      return 'bg-gray-100 text-gray-800 ring-gray-200';
  }
}

export function isPartBDpiaCode(code: string): boolean {
  return (DPIA_PART_B_CODES as readonly string[]).includes(String(code).trim().toUpperCase());
}

type RegisteredVendorApplicability = 'applicable' | 'not_applicable';

type DpiaRowLike = {
  code: string;
  applicability?: RegisteredVendorApplicability | null;
  item: { status?: string; response?: Record<string, unknown> | null } | null;
};

export function partBApplicabilityForOutcome(
  outcome: DpiaScreeningOutcome
): 'applicable' | 'not_applicable' | null {
  if (outcome.level === 'incomplete') return null;
  if (outcome.partBRequired) return 'applicable';
  if (outcome.level === 'not_required') return 'not_applicable';
  return null;
}

export function isPartBSubmittedOrApproved(rows: DpiaRowLike[]): boolean {
  let hasApplicable = false;
  for (const code of DPIA_PART_B_CODES) {
    const row = rows.find((r) => String(r.code).trim().toUpperCase() === code);
    if (!row || row.applicability !== 'applicable') continue;
    hasApplicable = true;
    const st = String(row.item?.status ?? 'draft');
    if (st !== 'pending_approval' && st !== 'approved') {
      return false;
    }
  }
  return hasApplicable;
}

export function isMandatoryPartBBlockingSubmit(
  outcome: DpiaScreeningOutcome,
  rows: DpiaRowLike[]
): boolean {
  return outcome.partBMandatory && !isPartBSubmittedOrApproved(rows);
}
